import { useState, useRef, useEffect } from "react";
import { Row, Col, Drawer } from "antd";
import { withTranslation, TFunction } from "react-i18next";
import { useLocation } from "react-router-dom";
import Container from "../../common/Container";
import { SvgIcon } from "../../common/SvgIcon";
import {
  HeaderSection,
  HeroSection,
  LogoContainer,
  Burger,
  NotHidden,
  Menu,
  CustomNavLinkSmall,
  Label,
  Outline,
  Span,
  BookingButton,
  MobileBookingButton,
} from "./styles";

const Header = ({ t }: { t: TFunction }) => {
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visible, setVisibility] = useState(false);

  // Get current locale from URL path
  const getLocaleFromPath = (pathname: string) => {
    const match = pathname.match(/^\/(en|es|it|de|fr)(\/|$)/);
    return match ? match[1] : "en";
  };
  
  const currentLocale = getLocaleFromPath(location.pathname);
  const bookingUrl = `https://www.tuscansunstays.com/${currentLocale}/booking/room#DatesGuests-BE`;

  useEffect(() => {
    setShowVideo(true); // evita SSR/rehydration issues
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Flags iOS ANTES de cualquier play
    v.muted = true;
    (v as any).defaultMuted = true;
    (v as any).playsInline = true;
    v.setAttribute("webkit-playsinline", "");

    const tryPlay = () =>
      v.play()
        .then(() => setIsPlaying(true))
        .catch(() => { /* silencioso */ });

    const onReady = () => { tryPlay(); };

    if (v.readyState >= 2) onReady();
    v.addEventListener("canplay", onReady, { once: true });
    v.addEventListener("canplaythrough", onReady, { once: true });

    // Reintentos suaves por si el evento se pierde
    const t1 = setTimeout(tryPlay, 600);
    const t2 = setTimeout(tryPlay, 1500);

    // Fallback: primera interacción del usuario en iOS
    const onTouch = () => { tryPlay(); window.removeEventListener("touchend", onTouch); };
    window.addEventListener("touchend", onTouch, { once: true });

    return () => {
      v.removeEventListener("canplay", onReady as any);
      v.removeEventListener("canplaythrough", onReady as any);
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("touchend", onTouch);
    };
  }, [showVideo]);

  const toggleButton = () => setVisibility(v => !v);

  const MenuItem = () => {
    const scrollTo = (id: string) => {
      const el = document.getElementById(id) as HTMLDivElement | null;
      el?.scrollIntoView({ behavior: "smooth" });
      setVisibility(false);
    };
    return (
      <>
        <CustomNavLinkSmall onClick={() => scrollTo("about")}><Span>{t("About")}</Span></CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={() => scrollTo("Tuscan Stay")}><Span>{t("Tuscan Stay")}</Span></CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={() => scrollTo("Our Apartments")}><Span>{t("Our Apartments")}</Span></CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={() => scrollTo("contact")}><Span>{t("Contact")}</Span></CustomNavLinkSmall>
        <CustomNavLinkSmall onClick={() => scrollTo("gallery")}><Span>{t("Gallery")}</Span></CustomNavLinkSmall>
      </>
    );
  };

  return (
    <HeroSection style={{ position: "relative", minHeight: "100vh", overflow: "hidden", background: "#000" }}>
      {/* Poster/fallback fuera del <video> para buen LCP y evitar pantalla negra */}
      <img
        src="/img/svg/hero.jpg"
        alt="La Lunaria di Lajatico"
        loading="eager"
        decoding="async"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", opacity: isPlaying ? 0 : 1, transition: "opacity .4s ease"
        }}
           
      />

      {showVideo && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/img/svg/hero.jpg"
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", zIndex: 0, opacity: isPlaying ? 1 : 0, transition: "opacity .4s ease"
          }}
        >
          {/* Mantén MP4; WEBM es opcional según soporte del target */}
          <source src="/img/svg/hero.mp4" type="video/mp4" />
          <source src="/img/svg/hero.webm" type="video/webm" />
        </video>
      )}

      <HeaderSection style={{ position: "relative", zIndex: 2 }}>
        <Container>
          <Row justify="space-between" align="middle">
            <LogoContainer to="/" aria-label="homepage">
              <SvgIcon src="logo_mono_green_landscape.svg" width="284px" height="70px" />
            </LogoContainer>
            <NotHidden>
              <MenuItem />
              <BookingButton 
                href={bookingUrl} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {t("Book Now")}
              </BookingButton>
            </NotHidden>
            <Burger onClick={toggleButton}><Outline /></Burger>
          </Row>

          <Drawer closable={false} open={visible} onClose={toggleButton}>
            <Col style={{ marginBottom: "2.5rem" }}>
              <Label onClick={toggleButton}>
                <Col span={12}><Menu>Menu</Menu></Col>
                <Col span={12}><Outline /></Col>
              </Label>
            </Col>
            <MenuItem />
            <MobileBookingButton 
              href={bookingUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => setVisibility(false)}
            >
              {t("Book Now")}
            </MobileBookingButton>
          </Drawer>
        </Container>
      </HeaderSection>
    </HeroSection>
  );
};

export default withTranslation()(Header);
